import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileText, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { adminApi, mediaUrl } from "../../../lib/api";
import { downloadFile } from "../../../lib/download";
import { formatApiErrorDetail, gbp, STOCK_STATUSES, STOCK_BADGE } from "../../../lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Switch } from "../../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

const EMPTY = { name: "", price: "", unit: "", category: "", description: "", is_available: true, image_base64: null, image_url: null, stock_status: "in_stock", is_featured: false };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.get("/admin/products"), adminApi.get("/customer/categories")])
      .then(([p, c]) => { setProducts(p.data); setCategories(c.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, price: p.price, unit: p.unit, category: p.category, description: p.description || "", is_available: p.is_available, image_base64: p.image_base64 || null, image_url: p.image_url || null, stock_status: p.stock_status || "in_stock", is_featured: !!p.is_featured });
    setModal(true);
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await adminApi.post("/admin/upload-image", fd);
      setForm((f) => ({ ...f, image_url: data.url, image_base64: null }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Image upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.unit || !form.category) {
      toast.error("Name, price, unit and category are required");
      return;
    }
    setSaving(true);
    const cat = categories.find((c) => c.name === form.category);
    const payload = { ...form, price: parseFloat(form.price), category_id: cat?.id || "", image_base64: form.image_base64 || "", image_url: form.image_url || "" };
    try {
      if (editing) await adminApi.put(`/admin/products/${editing.id}`, payload);
      else await adminApi.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setModal(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleAvail = async (p) => {
    await adminApi.put(`/admin/products/${p.id}`, { is_available: !p.is_available });
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_available: !x.is_available } : x)));
  };

  const doDelete = async () => {
    await adminApi.delete(`/admin/products/${deleteId}`);
    toast.success("Product deleted");
    setDeleteId(null);
    load();
  };

  const exportDocx = async () => {
    try {
      await downloadFile("/admin/products/export-docx", "ayome-products.docx");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{products.length} products</p>
        <div className="flex gap-2">
          <button data-testid="export-word-button" onClick={exportDocx} className="flex items-center gap-2 px-4 h-10 rounded-md border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
            <FileText size={16} /> Export to Word
          </button>
          <button data-testid="add-product-button" onClick={openNew} className="flex items-center gap-2 px-4 h-10 rounded-md bg-[#228B22] hover:bg-[#1A4331] text-white text-sm font-semibold transition-colors">
            <Plus size={16} /> Add Product
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#228B22]" size={28} /></div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Price</th>
                  <th className="px-4 py-3 font-semibold">Unit</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Stock</th>
                  <th className="px-4 py-3 font-semibold">Available</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-${p.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-[#0A1F13]">
                      <span className="inline-flex items-center gap-1.5">
                        {p.is_featured && <Star size={13} className="fill-[#FF9500] text-[#FF9500]" />}
                        {p.name}
                      </span>
                    </td>
                    <td className="px-4 py-3">{gbp(p.price)}</td>
                    <td className="px-4 py-3 text-gray-500">{p.unit}</td>
                    <td className="px-4 py-3 text-gray-500">{p.category}</td>
                    <td className="px-4 py-3">
                      {p.stock_status && p.stock_status !== "in_stock" ? (
                        <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${STOCK_BADGE[p.stock_status]?.cls || ""}`}>{STOCK_BADGE[p.stock_status]?.label}</span>
                      ) : (
                        <span className="text-xs text-gray-400">In stock</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Switch data-testid={`toggle-avail-${p.id}`} checked={p.is_available} onCheckedChange={() => toggleAvail(p)} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"><Pencil size={16} /></button>
                        <button data-testid={`delete-product-${p.id}`} onClick={() => setDeleteId(p.id)} className="p-1.5 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent data-testid="product-modal" className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Name"><input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (£)"><input data-testid="product-price-input" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inp} /></Field>
              <Field label="Unit"><input data-testid="product-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inp} placeholder="e.g. kg, pack" /></Field>
            </div>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-category-select" className="h-11"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description"><textarea data-testid="product-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp + " min-h-[70px] py-2"} /></Field>
            <Field label="Product Image">
              <div className="flex items-center gap-3">
                {(form.image_url || form.image_base64) && (
                  <img data-testid="product-image-preview" src={mediaUrl(form.image_url) || form.image_base64} alt="preview" className="w-16 h-16 rounded-md object-cover border border-gray-200" />
                )}
                {uploading && <Loader2 data-testid="product-image-uploading" size={18} className="animate-spin text-[#228B22]" />}
                <input data-testid="product-image-input" type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={handleImage} className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#E8F5E9] file:text-[#1A4331] file:font-semibold file:cursor-pointer" />
                {(form.image_url || form.image_base64) && (
                  <button type="button" data-testid="remove-product-image" onClick={() => setForm({ ...form, image_base64: null, image_url: null })} className="text-xs text-red-500 font-semibold">Remove</button>
                )}
              </div>
            </Field>
            <div className="flex items-center gap-2">
              <Switch data-testid="product-available-switch" checked={form.is_available} onCheckedChange={(v) => setForm({ ...form, is_available: v })} />
              <span className="text-sm text-gray-600">Available for sale</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="product-featured-switch" checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} />
              <span className="text-sm text-gray-600">Feature on storefront (Featured Picks)</span>
            </div>
            <Field label="Stock Status">
              <Select value={form.stock_status} onValueChange={(v) => setForm({ ...form, stock_status: v })}>
                <SelectTrigger data-testid="product-stock-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STOCK_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <button data-testid="save-product-button" onClick={save} disabled={saving} className="w-full h-11 rounded-md bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center justify-center transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : "Save Product"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete product?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-product" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const inp = "w-full h-11 px-3 rounded-md bg-gray-100 focus:bg-white border border-gray-200 focus:border-[#228B22] outline-none transition-colors";
function Field({ label, children }) {
  return <div><label className="text-sm text-gray-500 mb-1 block">{label}</label>{children}</div>;
}
